/**
 * Where pages sit on the composer canvas.
 *
 * World space is the world layer's layout px, the same px SP-146 measures with
 * `offsetTop` (the world-layer transform doesn't change it). Pages stack down
 * the world at x = 0, one `pagePitch` apart. `pageRect` is the only place page
 * origins come from; everything else asks it rather than multiplying by the
 * pitch.
 */
import { pageDimensions } from './paper';
import type { PageSetup } from './paper';
import type { Geometry } from './types';

/** World px between one page's bottom edge and the next page's top edge. */
export const PAGE_GAP = 48;

/** Screen-px height of a page's label row. */
export const PAGE_HEADER_HEIGHT = 24;

/** Below this zoom a page's label row would be taller than the gap and cover the page above it. */
export const PAGE_HEADER_MIN_ZOOM = PAGE_HEADER_HEIGHT / PAGE_GAP;

/** Distance between the tops of two neighbouring pages: page height plus `PAGE_GAP`. */
export function pagePitch(setup: PageSetup): number {
    return pageDimensions(setup).height + PAGE_GAP;
}

/** The box of page `index`. `index === pageCount` is the slot where a new page would go. */
export function pageRect(index: number, setup: PageSetup): Geometry {
    const { width, height } = pageDimensions(setup);
    return { x: 0, y: index * pagePitch(setup), width, height };
}

/** Slots `0…pageCount` (the pages plus the new-page slot), grown by half a page on
 *  every side. The camera centre is clamped into this box. */
export function canvasBounds(pageCount: number, setup: PageSetup): Geometry {
    const { width, height } = pageDimensions(setup);
    return {
        x: -width / 2,
        y: -height / 2,
        width: 2 * width,
        height: (pageCount + 1) * height + pageCount * PAGE_GAP + height,
    };
}

/**
 * The slot under world `worldY`:
 * - `0…pageCount − 1` is that page, with each gap split at its midpoint and
 *   anything above the first page counting as page 0;
 * - `pageCount` means strictly below the last page's bottom edge, i.e. the
 *   slot where a new page would go.
 */
export function slotIndexAt(worldY: number, pageCount: number, setup: PageSetup): number {
    const { height } = pageDimensions(setup);
    const pitch = pagePitch(setup);
    if (worldY > (pageCount - 1) * pitch + height) return pageCount;
    const index = Math.floor((worldY + PAGE_GAP / 2) / pitch);
    return Math.max(0, Math.min(pageCount - 1, index));
}

/** `slotIndexAt` clamped to the last page: the page closest to `worldY`. */
export function pageIndexNearest(worldY: number, pageCount: number, setup: PageSetup): number {
    return Math.min(slotIndexAt(worldY, pageCount, setup), Math.max(0, pageCount - 1));
}
